import { Button, Cascader, Form, Input, InputNumber, Select, Space, message } from "antd";
import { useEffect, useMemo, useState } from "react";
import { DatePicker as ZhDatePicker, Dialog as ZhDialog } from "zhihao-ui-react";

import { deliveryService } from "@/api/services/deliveryService";
import { shipService } from "@/api/services/shipService";
import { waybillService } from "@/api/services/waybillService";
import { areaLib, type AreaNode } from "@/utils/areaLib";
import { confirmDialog } from "@/utils/confirmDialog";
import { showRequestError } from "@/utils/requestErrorMessage";
import { AddressAutocomplete, getSuggestionAreaCodes, getSuggestionGeo } from "@/views/components/AddressAutocomplete";
import { WAYBILL_STATE } from "@/meta/logistics/waybill";
import type { AddressInfo, DeliveryForm, DeliveryVo, Order, Ship, TransportOrderShip } from "@zhihaoscm/entity";

import "./TradeOrderDeliveryDialog.css";

const PHONE_REGEXP = /^(((13[0-9])|(15[0-9])|(16[0-9])|(17[3-8])|(18[0-9])|(19[0-9])|(14[5-7]))\d{8})$/;

interface DeliveryDialogValues {
    scheduleDate: string;
    settlementAmount: number;
    estimatedTon: number;
    transportOrderShipId?: string;
    shipId?: string;
    contacts?: string;
    cellphone?: string;
    areaCode?: string[];
    detailAddress?: string;
    geo?: { lat: number; lng: number };
}

interface DeliveryOption {
    value: string;
    label: string;
    shipId?: string;
    shipName?: string;
}

interface AreaOption {
    value: string;
    label: string;
    children?: AreaOption[];
}

export interface TradeOrderDeliveryDialogProps {
    open: boolean;
    /** 提货详情页沿用 Vue 的卡片内编辑；订单详情新增提货仍使用弹窗。 */
    inline?: boolean;
    order: Order;
    editingDelivery?: DeliveryVo;
    onCancel: () => void;
    onSaved: () => void | Promise<void>;
    createDelivery?: (form: DeliveryForm) => Promise<unknown>;
    updateDelivery?: (id: string, form: DeliveryForm) => Promise<unknown>;
    loadWaybills?: (keyword: string) => Promise<TransportOrderShip[]>;
    loadShips?: (keyword: string) => Promise<Ship[]>;
}

/** 将 Vue areaLib 的 id/name/childs 结构转换为 Ant Cascader 选项。 */
function mapAreaOptions(items: AreaNode[]): AreaOption[] {
    return items.map(item => ({
        value: item.id,
        label: item.name,
        children: item.childs?.length ? mapAreaOptions(item.childs) : undefined
    }));
}

/** 订单详情新增提货弹窗，完整保留 Vue 的字段、联动、校验与提交契约。 */
export function TradeOrderDeliveryDialog({
    open,
    inline = false,
    order,
    editingDelivery,
    onCancel,
    onSaved,
    createDelivery = form => deliveryService.addDelivery(form),
    updateDelivery = (id, form) => deliveryService.updateDelivery(id, form),
    loadWaybills = keyword => waybillService.getSelector({ keyword, excludeScheduled: true }),
    loadShips = async keyword => (await shipService.getShipSelector({ keyword })).list || []
}: TradeOrderDeliveryDialogProps) {
    const [form] = Form.useForm<DeliveryDialogValues>();
    const [submitting, setSubmitting] = useState(false);
    const [waybillOptions, setWaybillOptions] = useState<DeliveryOption[]>([]);
    const [shipOptions, setShipOptions] = useState<DeliveryOption[]>([]);
    const [areaOptions, setAreaOptions] = useState<AreaOption[]>([]);
    const settlementAmount = Form.useWatch("settlementAmount", form);
    const estimatedTon = Form.useWatch("estimatedTon", form);
    const selectedWaybillId = Form.useWatch("transportOrderShipId", form);
    const contacts = Form.useWatch("contacts", form) || "";
    const selectedAreaCode = Form.useWatch("areaCode", form);
    const settlementUnitAmount = useMemo(() => {
        const amount = Number(settlementAmount || 0);
        const ton = Number(estimatedTon || 0);
        return amount > 0 && ton > 0 ? (amount / ton).toFixed(2) : "--";
    }, [estimatedTon, settlementAmount]);

    /** 旧版只在订单同时给出提货起止日期时限制日期选择。 */
    const disabledDeliveryDate = (date: Date): boolean => {
        if (!order.deliveryStartTime || !order.deliveryEndTime) return false;
        const day = new Date(date).setHours(0, 0, 0, 0);
        const start = new Date(order.deliveryStartTime).setHours(0, 0, 0, 0);
        const end = new Date(order.deliveryEndTime).setHours(0, 0, 0, 0);
        return day < start || day > end;
    };

    useEffect(() => {
        if (!open) return;
        // Vue 的 vo2Form 在编辑时回填整条提货记录，新增时才清空表单。
        if (editingDelivery?.delivery) {
            const delivery = editingDelivery.delivery;
            const address = delivery.addressInfo || ({} as AddressInfo);
            form.setFieldsValue({
                scheduleDate: delivery.scheduleDate?.slice(0, 10),
                settlementAmount: delivery.settlementAmount == null ? undefined : Number(delivery.settlementAmount),
                estimatedTon: Number(delivery.estimatedTon),
                transportOrderShipId: delivery.transportOrderShipId || undefined,
                shipId: delivery.shipId || undefined,
                contacts: address.contacts || "",
                cellphone: address.cellphone || "",
                areaCode: [address.province, address.city, address.district].filter(Boolean),
                detailAddress: address.detailAddress || "",
                geo: address.geo
            });
            setWaybillOptions(
                delivery.transportOrderShipId
                    ? [
                          {
                              value: String(delivery.transportOrderShipId),
                              label: String(editingDelivery.transportOrderShip?.id || delivery.transportOrderShipId),
                              shipId: delivery.shipId,
                              shipName:
                                  editingDelivery.transportOrderShip?.shipName ||
                                  delivery.shipInfo?.cnname ||
                                  delivery.shipInfo?.name
                          }
                      ]
                    : []
            );
            setShipOptions(
                delivery.shipId
                    ? [
                          {
                              value: String(delivery.shipId),
                              // 旧版 cnname 存在时仅回显中文船名，选项值仍使用 shipId 提交。
                              label: String(
                                  delivery.shipInfo?.cnname ||
                                      (delivery.shipInfo?.name
                                          ? `${delivery.shipInfo.name} | ${delivery.shipId}`
                                          : delivery.shipId)
                              )
                          }
                      ]
                    : []
            );
            // Vue 编辑表单打开后会立即呈现空结算金额的必填校验。
            if (delivery.settlementAmount == null) void form.validateFields(["settlementAmount"]).catch(() => undefined);
        } else {
            form.resetFields();
            setWaybillOptions([]);
            setShipOptions([]);
        }
        // 地区库异步加载完成后刷新级联选项，避免首次打开出现空白下拉。
        void areaLib.ready.then(() => setAreaOptions(mapAreaOptions(areaLib.getAreaList())));
    }, [editingDelivery, form, open]);

    /** 远程搜索可关联船运单，并保留船舶联动需要的原始字段。 */
    const searchWaybills = async (keyword: string) => {
        if (!keyword.trim()) return;
        try {
            const rows = await loadWaybills(keyword.trim());
            setWaybillOptions(
                rows.map(row => ({
                    value: String(row.id),
                    label: `${row.id} | ${WAYBILL_STATE.getIntroByCode(row.state) || "--"} | ${row.goodsType ?? "--"} | ${row.ton ?? "--"}`,
                    shipId: row.shipId,
                    shipName: row.shipName
                }))
            );
        } catch (error) {
            showRequestError(error, "关联运单查询失败");
        }
    };

    /** 选中运单后自动带出并锁定船舶，与 Vue deliveryDialog 一致。 */
    const changeWaybill = (value?: string) => {
        const matched = waybillOptions.find(option => option.value === value);
        form.setFieldValue("shipId", matched?.shipId);
        setShipOptions(matched?.shipId ? [{ value: matched.shipId, label: matched.shipName || matched.shipId }] : []);
    };

    /** 未关联运单时允许按船名或 AIS 号远程搜索船舶。 */
    const searchShips = async (keyword: string) => {
        if (!keyword.trim() || selectedWaybillId) return;
        try {
            const rows = await loadShips(keyword.trim());
            setShipOptions(
                rows.map(row => ({
                    value: String(row.id),
                    // 旧版 option label 表达式优先直接显示 cnname；仅无中文名时才拼接 ID。
                    label: row.cnname || `${row.name || "--"} | ${row.id}`
                }))
            );
        } catch (error) {
            showRequestError(error, "船舶查询失败");
        }
    };

    /** 校验并按旧版 hasInitiate 标志新增或修改提货，成功后刷新订单详情。 */
    const submitDelivery = async (hasInitiate: 0 | 1) => {
        let values: DeliveryDialogValues;
        try {
            values = await form.validateFields();
        } catch {
            return;
        }
        const confirmed = await confirmDialog(hasInitiate ? "是否保存并向买家发起提货" : "是否保存");
        if (!confirmed) return;
        setSubmitting(true);
        try {
            const [province = "", city = "", district = ""] = values.areaCode || [];
            const payload = {
                scheduleDate: `${values.scheduleDate.slice(0, 10)}T00:00:00`,
                settlementAmount: Number(values.settlementAmount),
                estimatedTon: Number(values.estimatedTon),
                settlementUnitAmount: Number(settlementUnitAmount === "--" ? 0 : settlementUnitAmount),
                transportOrderShipId: values.transportOrderShipId || "",
                shipId: values.shipId || "",
                productId: String(order.productId || ""),
                orderId: String(order.id),
                hasInitiate,
                addressInfo: {
                    contacts: values.contacts || "",
                    cellphone: values.cellphone || "",
                    areaCode: district,
                    province,
                    city,
                    district,
                    detailAddress: values.detailAddress || "",
                    geo: values.geo || editingDelivery?.delivery.addressInfo?.geo || { lat: 0, lng: 0 }
                }
            } as DeliveryForm;
            if (editingDelivery?.delivery) {
                // Vue 编辑接口使用原提货编号 PUT，不重新生成商品字段。
                payload.orderId = String(editingDelivery.delivery.orderId || order.id);
                await updateDelivery(String(editingDelivery.delivery.id), payload);
            } else {
                await createDelivery(payload);
            }
            message.success("已保存");
            onCancel();
            await onSaved();
        } catch (error) {
            showRequestError(error, "提货保存失败");
        } finally {
            setSubmitting(false);
        }
    };

    const actions = (
        <Space size={8}>
            <Button disabled={submitting} onClick={onCancel}>
                取消
            </Button>
            <Button loading={submitting} type="primary" onClick={() => void submitDelivery(0)}>
                保存
            </Button>
            <Button loading={submitting} type="primary" onClick={() => void submitDelivery(1)}>
                保存并发起
            </Button>
        </Space>
    );
    const formContent = (
            <Form<DeliveryDialogValues>
                className={`trade-order-delivery-dialog__form${inline ? " trade-order-delivery-inline__form" : ""}`}
                form={form}
                labelAlign="right"
                labelCol={{ flex: "92px" }}
                wrapperCol={{ flex: 1 }}
            >
                <Form.Item label="提货日期" name="scheduleDate" rules={[{ required: true, message: "请选择提货日期" }]}>
                    <ZhDatePicker
                        aria-label="提货日期"
                        disabledDate={disabledDeliveryDate}
                        placeholder="请选择提货日期"
                        type="date"
                        valueFormat="YYYY-MM-DD"
                    />
                </Form.Item>
                <Form.Item label="结算金额" required>
                    <div className="trade-order-delivery-dialog__number-field">
                        {/* 带单位的包装层不接收表单值；由内层 noStyle 项绑定实际 InputNumber。 */}
                        <Form.Item
                            name="settlementAmount"
                            noStyle
                            rules={[
                                { required: true, message: "请输入结算金额" },
                                { type: "number", min: 0.01, max: 9999999.99, message: "请输入0-10000000的数字" }
                            ]}
                        >
                            <InputNumber aria-label="结算金额" min={0} placeholder="请输入结算金额" />
                        </Form.Item>
                        <span>元</span>
                    </div>
                </Form.Item>
                <Form.Item label="提货吨数" required>
                    <div className="trade-order-delivery-dialog__number-field">
                        <Form.Item
                            name="estimatedTon"
                            noStyle
                            rules={[
                                { required: true, message: "请输入提货吨数" },
                                { type: "number", min: 0.01, max: 9999999.99, message: "请输入0-10000000的数字" }
                            ]}
                        >
                            <InputNumber aria-label="提货吨数" min={0} placeholder="请输入提货吨数" />
                        </Form.Item>
                        <span>吨</span>
                    </div>
                </Form.Item>
                <Form.Item label="结算单价">
                    <span className="trade-order-delivery-dialog__price">
                        {settlementUnitAmount === "--" ? "--" : `${settlementUnitAmount}元/吨`}
                    </span>
                </Form.Item>
                <Form.Item label="关联运单" name="transportOrderShipId">
                    <Select
                        aria-label="关联运单"
                        allowClear
                        filterOption={false}
                        options={waybillOptions}
                        placeholder="请选择关联运单"
                        showSearch
                        suffixIcon={null}
                        onChange={changeWaybill}
                        onSearch={keyword => void searchWaybills(keyword)}
                    />
                </Form.Item>
                <Form.Item label="船舶" name="shipId">
                    <Select
                        aria-label="船舶"
                        allowClear
                        disabled={Boolean(selectedWaybillId)}
                        filterOption={false}
                        options={shipOptions}
                        placeholder="请输入船舶名称或AIS号搜索"
                        showSearch
                        suffixIcon={null}
                        onSearch={keyword => void searchShips(keyword)}
                    />
                </Form.Item>
                <Form.Item label="收货人" name="contacts">
                    <Input
                        aria-label="收货人"
                        maxLength={32}
                        placeholder="请输入收货人"
                        suffix={<span className="trade-order-delivery-dialog__count">{contacts.length} / 32</span>}
                    />
                </Form.Item>
                <Form.Item
                    label="收货人手机号"
                    name="cellphone"
                    rules={[
                        {
                            validator: (_, value?: string) =>
                                !value || PHONE_REGEXP.test(value)
                                    ? Promise.resolve()
                                    : Promise.reject(new Error("请输入正确的手机号"))
                        }
                    ]}
                >
                    <Input aria-label="收货人手机号" placeholder="请输入收货人手机号" />
                </Form.Item>
                <Form.Item label="收货地址" name="areaCode">
                    <Cascader
                        aria-label="收货地址"
                        classNames={{ popup: { root: "trade-order-delivery-dialog__area-popup" } }}
                        options={areaOptions}
                        placeholder="请选择"
                        showSearch
                    />
                </Form.Item>
                <Form.Item className="trade-order-delivery-dialog__detail-address" name="detailAddress">
                    <AddressAutocomplete
                        aria-label="详细地址"
                        regionCode={selectedAreaCode?.[2] || ""}
                        onSelectSuggestion={suggestion => {
                            const areaCodes = getSuggestionAreaCodes(suggestion);
                            const geo = getSuggestionGeo(suggestion);
                            // 未选择行政区时允许候选地址补齐省市区，始终同步最新坐标。
                            if (!selectedAreaCode?.[2] && areaCodes.length) form.setFieldValue("areaCode", areaCodes);
                            if (geo) form.setFieldValue("geo", { lat: geo.lat, lng: geo.lng });
                        }}
                    />
                </Form.Item>
            </Form>
    );

    if (!open) return null;
    if (inline) {
        return (
            <section className="section-card trade-order-delivery-inline" aria-label="修改提货">
                <div className="trade-order-delivery-inline__header">
                    <div className="section-title mb-0">提货信息</div>
                    {actions}
                </div>
                {formContent}
            </section>
        );
    }

    return (
        <ZhDialog
            modalClass="trade-order-delivery-dialog"
            open={open}
            title={editingDelivery ? "修改提货" : "新增提货"}
            width={920}
            onCancel={onCancel}
            footer={actions}
        >
            {formContent}
        </ZhDialog>
    );
}
